import { useState, useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import Drawer from 'UiKit/drawer';
import { TextBold, TextRegular, TextSemiBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import Badge from 'UiKit/badge';
import InputSelectComponent from 'UiKit/input-select';
import { Iteration } from 'Component/others';
import useGetBrand from 'Domain/brand/useGetBrand';
import useGetStockStatus from 'Domain/stock-promo/useGetStockStatus';
import useGetPromoStatus from 'Domain/stock-promo/useGetPromoStatus';
import useGetHostAccountPromo from 'Domain/host-stock-promo/useGetHostAccountPromo';
import StoreBadge from 'View/host-stock-promo/component/store-badge';
import { toggleMultipleFilter, toggleSingleFilter } from 'View/host-stock-promo/helper/useFilterToggle';
import { getAvailableOptions } from 'View/host-stock-promo/helper/filterOptions';
import { useHostStockPromoState } from 'View/host-stock-promo/state';

import styles from './styles.module.css';

const PromoFilterDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation('host-stock-promo');
  const { filterState, updateFilterState } = useHostStockPromoState();
  const { data: brand } = useGetBrand();
  const { data: storeOptions } = useGetHostAccountPromo({ enabled: isOpen });
  const { data: stockStatusOptions } = useGetStockStatus();
  const { data: promoStatusOptions } = useGetPromoStatus();

  const [tempFilters, setTempFilters] = useState({
    brandId: [],
    store: [],
    stockStatus: '',
    promoStatus: [],
  });

  useEffect(() => {
    if (isOpen) {
      setTempFilters({
        brandId: filterState?.brandId || [],
        store: filterState?.store || [],
        stockStatus: filterState?.stockStatus || '',
        promoStatus: filterState?.promoStatus || [],
      });
    }
  }, [isOpen, filterState?.brandId, filterState?.store, filterState?.stockStatus, filterState?.promoStatus]);

  const isBrandActive = (brandValue) => {
    return tempFilters?.brandId.includes(brandValue);
  };

  const isStockStatusActive = (stockStatusValue) => {
    return tempFilters?.stockStatus === stockStatusValue;
  };

  const isPromoStatusActive = (promoStatusValue) => {
    return tempFilters?.promoStatus.includes(promoStatusValue);
  };

  const availableStoreOptions = getAvailableOptions(storeOptions, tempFilters?.store);

  const handleReset = () => {
    setTempFilters({ brandId: [], store: [], stockStatus: '', promoStatus: [] });
    updateFilterState({ brandId: [], store: [], stockStatus: '', promoStatus: [], page: 1 });
    onClose();
  };

  const handleApply = () => {
    updateFilterState({
      brandId: tempFilters?.brandId,
      store: tempFilters?.store,
      stockStatus: tempFilters?.stockStatus,
      promoStatus: tempFilters?.promoStatus,
      page: 1,
    });
    onClose();
  };

  return (
    <Drawer height={'85vh'} className={styles['promo-filter-drawer-container']} isOpen={isOpen} onClose={onClose} draggable={true}>
      {/* Header */}
      <Flex className={styles['promo-filter-drawer__head-section']}>
        <TextBold fontSize="18px" color="#1A1A1A">
          {t('filter.title')}
        </TextBold>
      </Flex>

      {/* Content */}
      <Box className={styles['promo-filter-drawer__content-section']}>
        {/* Filters */}
        <Box className={styles['promo-filter-drawer__filter-section-container']}>
          <Flex gap="8px" direction="column">
            <TextRegular fontSize="16px" color="#1A1A1A">
              {t('filter.brand')}
            </TextRegular>
            <Box className={styles['promo-filter-drawer__filter-section']}>
              <Iteration
                items={brand}
                render={(item) => (
                  <Badge
                    key={item?.id}
                    variant="outlined"
                    className={`${styles['filter-host-stock-promo__select-badge']} ${
                      isBrandActive(item?.value) ? styles['filter-host-stock-promo__select-badge--active'] : ''
                    }`}
                    onClick={() => toggleMultipleFilter(item?.value, setTempFilters, 'brandId')}
                  >
                    {item?.value}
                  </Badge>
                )}
              />
            </Box>
          </Flex>
          <Flex gap="8px" direction="column">
            <TextRegular fontSize="16px" color="#1A1A1A">
              {t('filter.store_account')}
            </TextRegular>
            <InputSelectComponent
              placeholder={t('filter.store_account_placeholder')}
              options={availableStoreOptions}
              className={styles['promo-filter-drawer__filter-store']}
              field={{
                onChange: (value) => {
                  toggleMultipleFilter(value, setTempFilters, 'store');
                },
              }}
              value={null}
              minHeight="28px"
            />
            <Box className={styles['promo-filter-drawer__filter-store-badge-container']}>
              <Iteration
                items={tempFilters?.store}
                render={(item) => <StoreBadge key={item?.value} store={item} onClose={() => toggleMultipleFilter(item, setTempFilters, 'store')} />}
              />
            </Box>
          </Flex>
          <Flex gap="8px" direction="column">
            <TextRegular fontSize="16px" color="#1A1A1A">
              {t('filter.stock_status')}
            </TextRegular>
            <Box className={styles['promo-filter-drawer__filter-section']}>
              <Iteration
                items={stockStatusOptions}
                render={(item) => (
                  <Badge
                    key={item?.value}
                    variant="outlined"
                    className={`${styles['filter-host-stock-promo__select-badge']} ${
                      isStockStatusActive(item?.value) ? styles['filter-host-stock-promo__select-badge--active'] : ''
                    }`}
                    onClick={() => toggleSingleFilter(item?.value, setTempFilters, 'stockStatus')}
                  >
                    {item?.label}
                  </Badge>
                )}
              />
            </Box>
          </Flex>
          <Flex gap="8px" direction="column">
            <TextRegular fontSize="16px" color="#1A1A1A">
              {t('filter.promo_status')}
            </TextRegular>
            <Box className={styles['promo-filter-drawer__filter-section']}>
              <Iteration
                items={promoStatusOptions}
                render={(item) => (
                  <Badge
                    key={item?.value}
                    variant="outlined"
                    className={`${styles['filter-host-stock-promo__select-badge']} ${
                      isPromoStatusActive(item?.value) ? styles['filter-host-stock-promo__select-badge--active'] : ''
                    }`}
                    onClick={() => toggleMultipleFilter(item?.value, setTempFilters, 'promoStatus')}
                  >
                    {item?.label}
                  </Badge>
                )}
              />
            </Box>
          </Flex>
        </Box>

        {/* Footer Buttons */}
        <Flex className={styles['promo-filter-drawer__footer-section']}>
          <ButtonOutline onClick={handleReset}>
            <TextSemiBold color="var(--color-info70)">{t('common:reset')}</TextSemiBold>
          </ButtonOutline>
          <ButtonSolid onClick={handleApply}>
            <TextSemiBold color="var(--color-semantic-white)">{t('common:apply')}</TextSemiBold>
          </ButtonSolid>
        </Flex>
      </Box>
    </Drawer>
  );
};

export default PromoFilterDrawer;
